import {
  HYGIENE_CHECK_STATUSES,
  type HygieneCompleted,
  type KnownHygieneBand,
  type KnownHygienePriority
} from '../types/hygiene';
import { humanizeSlug } from './format';
import {
  HEALTH_BAND_LABELS,
  type HealthAssessment,
  type HealthBand,
  type HealthDriver,
  type HealthImprovement
} from './derive-health';
import {
  assessmentNotes,
  categoryDetailLine,
  categoryInsight,
  categoryTitle,
  checkExplains,
  checkShortTitle,
  checkTitle,
  checkVisual,
  humanizeReference
} from './hygiene-presentation';

/**
 * Maps the canonical backend hygiene assessment onto the health-card view
 * model. The backend is the score authority: scores, bands, coverage, caps
 * and actions pass through untouched — this file only reshapes.
 */

const BAND_MAP: Record<KnownHygieneBand, HealthBand> = {
  strong: 'strong',
  sound: 'sound',
  needs_attention: 'needs-attention',
  at_risk: 'at-risk',
  insufficient_evidence: 'insufficient-evidence'
};

const STATE_BY_BAND: Record<KnownHygieneBand, HealthDriver['state']> = {
  strong: 'sound',
  sound: 'sound',
  needs_attention: 'watch',
  at_risk: 'at-risk',
  insufficient_evidence: 'gathering'
};

const PRIORITY_ORDER: Record<KnownHygienePriority, number> = {
  now: 0,
  soon: 1,
  monitor: 2
};
const PRIORITY_LABEL: Record<KnownHygienePriority, string> = {
  now: 'Now',
  soon: 'Soon',
  monitor: 'Monitor'
};

function knownBand(band: string): KnownHygieneBand | undefined {
  return Object.hasOwn(BAND_MAP, band) ? (band as KnownHygieneBand) : undefined;
}

/**
 * The band as the card shows it. The band vocabulary can grow: a band this
 * build has never seen keeps the served score, reads as its own words, and
 * is styled neutrally, because the app does not know whether it is good.
 */
function presentBand(band: string): { band: HealthBand; label: string } {
  const known = knownBand(band);
  return known
    ? { band: BAND_MAP[known], label: HEALTH_BAND_LABELS[BAND_MAP[known]] }
    : {
        band: 'insufficient-evidence',
        label: humanizeSlug(band).toUpperCase()
      };
}

function knownPriority(
  priority: string | undefined
): KnownHygienePriority | undefined {
  return priority != null && Object.hasOwn(PRIORITY_ORDER, priority)
    ? (priority as KnownHygienePriority)
    : undefined;
}

const EVALUATED_STATUSES = new Set(['met', 'partially_met', 'not_met']);

export function hygieneToAssessment(
  hygiene: HygieneCompleted
): HealthAssessment {
  const { band, label: bandLabel } = presentBand(hygiene.summary.band);

  const drivers: HealthDriver[] = hygiene.categories.map((category) => {
    const applicable = category.checks.filter(
      (check) => check.status !== 'not_applicable'
    );
    const evaluated = applicable.filter((check) =>
      EVALUATED_STATUSES.has(check.status)
    );
    const categoryBand = knownBand(category.band);
    // A scored category in a band this build does not know stays neutral.
    const state =
      category.score == null || categoryBand == null
        ? 'gathering'
        : STATE_BY_BAND[categoryBand];

    return {
      id: category.id,
      label: categoryTitle(category),
      weight: Number(category.weight),
      score: category.score == null ? null : Math.round(category.score),
      state,
      // Quiet sentence case for absence; scored bands keep their caps voice.
      stateLabel:
        category.score == null
          ? evaluated.length > 0
            ? 'Partial data'
            : 'Waiting on data'
          : presentBand(category.band).label,
      detail: categoryDetailLine(category, hygiene),
      insight: categoryInsight(category),
      checkStates: applicable.map((entry) => checkVisual(entry))
    };
  });

  return {
    score: hygiene.summary.score,
    band,
    bandLabel,
    provisional: hygiene.summary.provisional,
    coverageBps: hygiene.summary.coverageBps,
    drivers,
    source: 'canonical',
    attribution: `${hygiene.definition.referenceProfile.label} · definition v${hygiene.definition.version}`,
    caps: hygiene.summary.appliedCaps.map((cap) => cap.explanation),
    refreshing: hygiene.status === 'refreshing',
    confidence: hygiene.confidence,
    // A withheld score must explain itself — in the reader's language, and
    // saying whose move it is. Raw system messages go to the dialog only.
    evidenceNotes:
      hygiene.summary.score == null
        ? assessmentNotes(hygiene).slice(0, 2)
        : undefined,
    evidenceDetails:
      hygiene.summary.score == null
        ? hygiene.provenance.warnings.map((warning) => warning.message)
        : undefined
  };
}

/** Where each practice area's work happens, when the check names no route. */
const CATEGORY_ROUTE: Record<string, string> = {
  concentration_diversification: '/risk#diversification',
  liquidity_resilience: '/risk#liquidity',
  risk_evidence: '/risk'
};

/**
 * The improvement rule: a card derives ONLY from a scored check with
 * status `not_met` and reason `REFERENCE_NOT_MET` — by construction an
 * allocator-actionable practice measured against a reference the allocator
 * can move toward. Pending evidence, coverage gaps, and wiring are never
 * opportunities; they belong to the confidence surface, not homework.
 */
export function hygieneToImprovements(
  hygiene: HygieneCompleted
): HealthImprovement[] {
  const improvements: {
    priority: KnownHygienePriority;
    improvement: HealthImprovement;
  }[] = [];

  for (const category of hygiene.categories) {
    for (const check of category.checks) {
      if (
        check.status !== 'not_met' ||
        !check.reasonCodes.includes('REFERENCE_NOT_MET')
      ) {
        continue;
      }

      const action = check.action;
      const visual = checkVisual(check);
      // A priority this build has never seen falls back to the severity.
      const priority: KnownHygienePriority =
        knownPriority(action?.priority) ??
        (check.severity === 'critical'
          ? 'now'
          : check.severity === 'attention'
            ? 'soon'
            : 'monitor');
      const href =
        action?.destination.type === 'route' && action.destination.href
          ? action.destination.href
          : (CATEGORY_ROUTE[category.id] ?? '/risk');

      improvements.push({
        priority,
        improvement: {
          id: check.id,
          title:
            action?.label ??
            `Bring ${checkShortTitle(check).toLowerCase()} within the guideline`,
          detail:
            visual.current != null && visual.guideline != null
              ? `${visual.current} today vs guideline ${visual.guideline}. ${checkExplains(check)}`
              : checkExplains(check),
          driverDelta: null,
          scoreDelta: null,
          impactLabel: PRIORITY_LABEL[priority],
          resultLabel: checkTitle(check),
          tradeoff: `Guideline · ${humanizeReference(check.reference.display)}`,
          actionLabel: 'Review',
          href
        }
      });
    }
  }

  return improvements
    .sort(
      (left, right) =>
        PRIORITY_ORDER[left.priority] - PRIORITY_ORDER[right.priority]
    )
    .map((entry) => entry.improvement);
}

/**
 * What the improvements panel may say when it has no card to show.
 *
 * - `all-clear`: at least one check has data and every one of them is
 *   inside its guideline. Only this earns the green "nothing to improve".
 * - `attention`: a check with data is not fully met (partly met, or missed
 *   for a reason that produces no card). Saying "nothing to improve" beside
 *   it would be false.
 * - `unrecognised`: nothing known is off guideline, but an applicable check
 *   carries a status this build has never seen. Its meaning is unknown, so
 *   neither "nothing to improve" nor "something is off" can be claimed.
 * - `unassessed`: no check has data yet, so nothing was assessed either way.
 *
 * All clear is reserved for an assessment whose every applicable check has
 * a status this build knows, with every one that has data `met`.
 */
type ImprovementsEmptyState =
  | 'all-clear'
  | 'attention'
  | 'unrecognised'
  | 'unassessed';

const KNOWN_CHECK_STATUSES = new Set<string>(HYGIENE_CHECK_STATUSES);

export function hygieneImprovementsEmptyState(
  hygiene: HygieneCompleted
): ImprovementsEmptyState {
  const applicable = hygiene.categories
    .flatMap((category) => category.checks)
    .filter((check) => check.status !== 'not_applicable');
  const evaluated = applicable.filter((check) =>
    EVALUATED_STATUSES.has(check.status)
  );
  // A known miss is a fact whatever else is on the list.
  if (evaluated.some((check) => check.status !== 'met')) return 'attention';
  if (applicable.some((check) => !KNOWN_CHECK_STATUSES.has(check.status))) {
    return 'unrecognised';
  }
  return evaluated.length === 0 ? 'unassessed' : 'all-clear';
}
